/**
 * FloatingVoiceBar Component
 * Phase P2.4-1: Voice Overlay UX Redesign (Non-Blocking Mode)
 *
 * Uses the same voice bar design pattern as seen in Explore, Room Service, and Spa cards.
 * Positioned to appear where the "Press Space to speak" hint is in the concierge section.
 * Uses fixed positioning so it works in both normal and TV fullscreen modes.
 *
 * Design principles:
 * - Consistent with existing card voice bars
 * - Always present, never intrusive
 * - Minimal visual footprint
 * - Smooth state transitions
 * - Non-blocking: concierge remains visible and interactive
 */

import { motion, AnimatePresence } from 'framer-motion';
import { Mic } from 'lucide-react';
import type { VoiceState } from './GlobalVoiceOverlay';

export interface FloatingVoiceBarProps {
  /** Current voice state */
  voiceState: VoiceState;

  /** Live transcript or status message */
  transcript: string;

  /** Status message when speaking */
  statusMessage?: string;

}

export function FloatingVoiceBar({
  voiceState,
  transcript,
  statusMessage,
}: FloatingVoiceBarProps) {
  // Don't render if idle
  if (voiceState === 'idle') return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        className="fixed z-[9999] pointer-events-auto flex items-center justify-center gap-3"
        style={{
          left: 'calc((100vw / 6.5) * 1 + 2rem)',
          bottom: '5.5rem',
          width: 'calc((100vw / 6.5) * 4 - 4rem)',
        }}
      >
        {voiceState === 'listening' && (
          <>
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1 }}
              className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center"
            >
              <Mic className="w-4 h-4 text-red-400" />
            </motion.div>
            <span className="text-sm text-white/60">
              {transcript || 'Listening...'}
            </span>
          </>
        )}
        {voiceState === 'processing' && (
          <span className="text-sm text-white/60">Processing...</span>
        )}
        {voiceState === 'speaking' && statusMessage && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-zaxia-teal"
          >
            {statusMessage}
          </motion.span>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export default FloatingVoiceBar;
